/**
 * The clients behind a figure on the client report.
 *
 * Every number on that page opens this, and it opens on the rows the
 * number was built from rather than on a rule for finding them again — a
 * sheet that recomputed "not in for 30 days" from the whole book would
 * open on a thousand rows under a heading that said four hundred. See
 * `cutOf` in domain/clientReport.js for the cuts a figure can name.
 *
 * ## One scope, five tabs
 *
 * A figure names a group of accounts: a plan, a promo code, a window, or
 * everybody. That group is the sheet's scope, and the tabs are the five
 * cuts of it people ask for next — who paid, who did not, who came in on
 * a code, who has been in this week. Whoever opens "SUITE plan" usually
 * wants the paying half a moment later, and should not have to close
 * anything to get there.
 *
 * Opening a row opens the whole client record on top of this, so the
 * drill-down is still behind it when that closes. The overlay deliberately
 * sits one layer below the workspace's sheets for that reason.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import { Avatar, EmptyState, Pill, PayPill, StatusPill } from '../Primitives.jsx';
import { cutOf, promoOf, ownerOf, planOf, seenText, windowLabel } from '../../domain/clientReport.js';

/* The five cuts, with "everybody" first. Each carries its own test, so a
   tab and the rows it shows cannot come apart. The activity one is built
   per sheet, because it names whichever window the page is asking about. */
const tabsFor = days => [
  ['all', 'Everyone', () => true],
  ['paid', 'Payment done', x => !!x.d.paid],
  ['unpaid', 'Not paid', x => !x.d.paid],
  ['promo', 'On a promo code', x => !!promoOf(x.r)],
  [
    'seen',
    days === 1 ? 'In today' : `In the last ${days} days`,
    x => x.d.dAct !== null && x.d.dAct <= days
  ]
];

const notesFor = days => ({
  all: 'Every account in this cut, newest sign-up first.',
  paid: 'The payment column says they have paid. What that is worth is on the right of each row.',
  unpaid: 'Nothing recorded yet. Most of a free book is here, and that is not a debt.',
  promo: 'They arrived on a code. A code used many times is a channel somebody owns.',
  seen: `They have opened XYRA ${windowLabel(days)}, whatever the status column says.`
});

export default function ClientDetail({ cut, report, period, days = 7, onClose }) {
  const { S, fmtMoney, fmtDate, setSheet } = useApp();
  const scope = useMemo(() => cutOf(report, cut), [report, cut]);
  const TABS = useMemo(() => tabsFor(days), [days]);
  const NOTES = useMemo(() => notesFor(days), [days]);
  /* A figure that named a tab opens on it — see `cutOf`. Read once, on
     mount, because the sheet covers the page that would change it. */
  const [tab, setTab] = useState(() => (scope && scope.tab) || 'all');
  const [q, setQ] = useState('');

  /* Escape closes it, the way the shared sheet shell does. The footer
     button that used to be the other way out has gone - the X is the
     only one on screen, and a window with one way out had better take
     the key everybody presses. */
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* Counted once, so every tab can carry its own number and an empty one
     can be stepped back before anybody clicks it. */
  const buckets = useMemo(() => {
    const rows = scope ? scope.rows : [];
    return Object.fromEntries(TABS.map(([k, , test]) => [k, rows.filter(test)]));
  }, [scope, TABS]);

  const here = buckets[tab] || [];

  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return here;
    return here.filter(x => [
      x.r.name, x.r.email, x.r.phone, x.r.contact, planOf(x.r), x.r.status,
      promoOf(x.r), ownerOf(x.r), x.r.source
    ].filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [here, q]);

  if (!scope) return null;

  /* What this tab is worth, where adding it up means anything: only over
     the accounts with a payment recorded, which is what the figures on the
     page behind say too. */
  const value = here.reduce((t, x) => t + (x.d.paid ? Number(x.d.amt || 0) : 0), 0);
  /* How much of this tab the total could not account for. */
  const blanks = here.filter(x => x.d.paid && !x.d.amt).length;

  return (
    <div className="cdwrap" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="cdsheet" onClick={e => e.stopPropagation()}>
        <header className="cdhead">
          <div className="ic g-green"><Icon name="users" solid /></div>
          <div className="cdtitle">
            <h3>{scope.title}</h3>
            <p>{scope.sub} · {period.label}</p>
          </div>
          <button type="button" className="cdx" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
        </header>

        {scope.tabs === false ? null : (
        <nav className="cdtabs" role="tablist">
          {TABS.map(([k, label]) => {
            const n = (buckets[k] || []).length;
            return (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                className={`cdtab ${k}${tab === k ? ' on' : ''}${n ? '' : ' nil'}`}
                onClick={() => { setTab(k); setQ(''); }}
                disabled={!n}
              >
                {label}<span>{n.toLocaleString()}</span>
              </button>
            );
          })}
        </nav>
        )}

        <div className="cdtop">
          <b>{here.length.toLocaleString()}</b>
          <span>client{here.length === 1 ? '' : 's'}</span>
          {value ? <strong className="cdval">{fmtMoney(value)}</strong> : null}
          {blanks ? (
            <strong className="cdval warn" title="The payment column says yes and the record carries no figure">
              {blanks} with no amount
            </strong>
          ) : null}
          <em>{NOTES[tab]}</em>
          <div className="cdfind">
            <Icon name="search" />
            <input value={q} placeholder="Find a client…" onChange={e => setQ(e.target.value)} />
          </div>
        </div>

        <div className="cdbody">
          {!list.length ? (
            <EmptyState icon="search" tone="g-slate" title={q ? 'Nothing matches that' : 'No rows on this tab'} />
          ) : (
            <ol className="cdlist">
              {list.map(({ r, d }) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className="cdrow"
                    onClick={() => setSheet({ kind: 'clientView', id: r.id })}
                    title="Open the whole record"
                  >
                    <Avatar name={r.name} />

                    <div className="cdwho">
                      <b>{r.name || 'Untitled'}</b>
                      <small>{r.email || r.contact || 'no address on the record'}</small>
                      {d.flags.length ? <i className="cdflag"><Icon name="alert" />{d.flags[0].txt}</i> : null}
                    </div>

                    <div className="cdtags">
                      <Pill tone="gr-violet" className="cdp">{planOf(r)}</Pill>
                      <StatusPill value={r.status} />
                      <PayPill value={r.pay} paidValues={S.paidValues} />
                      {promoOf(r) ? <Pill tone="gr-amber" icon="tag" className="cdp">{promoOf(r)}</Pill> : null}
                    </div>

                    <div className="cdown">
                      <span>{ownerOf(r)}</span>
                      <small>{r.regOn ? `joined ${fmtDate(d.reg)}` : 'no sign-up date'}</small>
                    </div>

                    {/*
                      * A paying account with nothing on it is not worth
                      * zero, it is unrecorded — and "AED 0" beside a green
                      * YES reads as a contradiction rather than as the
                      * missing figure it actually is.
                      */}
                    <div className="cdamt">
                      {d.paid && !d.amt
                        ? <span className="cdnil">No amount</span>
                        : <span>{d.paid ? fmtMoney(d.amt) : '—'}</span>}
                      <small>{d.paid ? 'recorded' : 'nothing recorded'}</small>
                    </div>

                    {/*
                      * The date active and not active are worked out from.
                      *
                      * "4d ago" is the figure the cards are counted on, so it
                      * leads; the day itself is underneath, because a list of
                      * accounts to chase is worked through by date and
                      * "somebody was in 47 days ago" is not a date anybody can
                      * put in a calendar.
                      */}
                    <div className="cdseen">
                      <span className={d.dAct === null ? 'bad' : ''}>{seenText(d)}</span>
                      <small>{d.act ? fmtDate(d.act) : 'no activity on the record'}</small>
                    </div>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>

        <footer className="cdfoot">
          <span className="dim">
            {list.length.toLocaleString()} shown of {here.length.toLocaleString()}
            {' · open any row for the plan, the payments and the whole timeline'}
          </span>
        </footer>
      </div>
    </div>
  );
}
